'use strict';

import $ from 'jquery';
import config from '../js/config';
import utilities from '../js/utilities';
import _ from 'underscore';
import cookies from 'js-cookie';
import mediator from '../js/mediator';
import Analytics from '../components/Analytics/Analytics';
import { makeRequest, hybrisMakeRequest } from '../utilities/makeRequest';
import * as featureFlags from '../utilities/featureFlags';
import cacheCookieHelper from '../components/CacheCookieHelper/CacheCookieHelper';
import cartApi from './CartAPI';

export class CartUserService {

  constructor() {
    this.userInfoFetchRequest = null;
    this.userInfo = null;
    this.userInfoError = null;
  }

  _getMyArrowCookie() {
    return cookies.get(config.cookies.myArrow);
  }

  isMyArrowUser() {
    return new Promise((resolve) => {
      if (!this.userInfo) {
        this.getUserInfo().then(_userInfo => {
          resolve(_userInfo.creditLimit > 0);
        });
      } else {
        resolve(this.userInfo.creditLimit > 0);
      }
    });
  }

  setUserName(email) {
    this.set({ username: email });
  }

  setFirstAndLastNameFromFullName(fullName) {
    if (fullName) {
      var nameArray = fullName.trim().split(' ');
      this.set({ firstname: nameArray[0] });
      var lastName = null;

      // If the array is longer than 1 it at least has one more element there is a last name
      if (nameArray.length > 1) {

        // Remove the first name from the array
        nameArray.splice(0, 1);
        for (var i = 0; i < nameArray.length; i++) {
          lastName = lastName ? ' ' + nameArray[i] : nameArray[i];
        }
        lastName.trim();
        this.set({ lastname: lastName });
      } else {
        // Set lastname to firstname for API as lastname is required
        this.set({ lastname: this.firstname });
      }
    }
  }

  setRegionCookie(region) {
    cookies.set(config.cookies.region, region);
    if (cacheCookieHelper) {
      cacheCookieHelper.setCacheCookie();
    }
  }

  getRegionCookie() {
    return cookies.get(config.cookies.region);
  }

  /**
   * This will return the region from the values used in the
   * select region dropdown.
   *
   * They will be full urls like http://components.arrow.com/part/search/1?region=arrowna
   *
   * @param {string} urlString
   * @return {string}
   */
  getRegionFromUrlValue(urlString) {
    var params = utilities.deserialize(urlString);
    // find prop name that has some variation of 'region'
    for (var propName in params) {
      if (propName.toLowerCase().indexOf('region') > -1) {
        return params[propName];
      }
    }
  }

  /**
   * Check to see if the arrow user is currently
   * logged in based on the arrowfed cookie
   *
   * @return {*}
   */
  isUserLoggedIn() {
    return window.Arrow.isAuthorized;
  }

  /**
  * Check to see if we have userInfo from the cart api
  * and if the user is a guest
  *
  * @return {*|null|boolean}
  */
  isCartUserAGuest() {
    return !!this.userInfo && this.userInfo.fullName === 'Guest';
  }

  getCurrentUserInfoRequest() {
    let shouldStartNewRequest = true;
    if (this.userInfoFetchRequest) {
      if (!this.userInfoFetchRequest.isActivated()) shouldStartNewRequest = false;
      if (this.userInfoFetchRequest.isRunning) shouldStartNewRequest = false;
    }
    if (shouldStartNewRequest) {
      const requestOptions = {
        url: config.urls.cart.getUserInfo,
        dataType: 'jsonp',
        timeout: 20000,
        data: {
          format: 'jsonp'
        },
        retryOptions: {
          enabled: true,
          attempts: 2
        },
        handleErrorLocally: true
      };
      this.userInfoFetchRequest = makeRequest(requestOptions);
      this.userInfoFetchRequest.use((request) => {
        const data = request.response;
        this.userInfo = data;
        if (this.userInfo) {
          let userData = { custNumber: this.userInfo.custNumber ? this.userInfo.custNumber : '-1' };
          let userObject = {
            user: {
              profile: userData
            }
          };
          Analytics.setOnDataLayer(userObject);
        }
        mediator.trigger(config.events.cart.userInfoSet, this.userInfo);
      });
    }
    return this.userInfoFetchRequest;
  }

  /**
   * Get Logged in Customer Info- this is similar to getUserInfo payload but if CSR logs in for a customer,
   * then this api would return signed in Customer info
   * @returns {Promise<RequestPipeline>}
   */
  getCustomerInfo() {
    const requestOptions = {
      url: config.urls.cart.getCustomerInfo,
      dataType: 'jsonp',
      data: { format: 'jsonp' }
    };
    return makeRequest(requestOptions)
      .use()
      .run()
      .then((request) => {
        return request.response;
      })
      .catch(error => {
        throw error;
      });
  }

  /**
   * CSR Customer Checkout
   * @param string email- A customer email address to verify if it exists.
   * @param string customerName - Customer Name is optional
   * @returns {Promise<RequestPipeline>}
   */
  csrCustomerCheckout(email, customerName) {
    const requestOptions = {
      url: config.urls.cart.csrCustomerCheckout,
      dataType: 'jsonp',
      data: { format: 'jsonp', custEmail: email, custName: customerName }
    };
    return makeRequest(requestOptions)
      .use()
      .run()
      .then((request) => {
        return request.response;
      })
      .catch(error => {
        throw error;
      });
  }

  /**
   * Check if User exists
   * @param string email- A customer email address to verify if it exists.
   * @param string password - This is hard coded paswword for all users need to be included
   * @returns {Promise<RequestPipeline>}
   */
  checkIfUserExists(email, requestFn) {
    if (!requestFn) {
      requestFn = makeRequest;
    }
    const requestOptions = {
      method: 'POST',
      url: config.urls.registration.userCheck,
      dataType: 'json',
      data: { fmt: 'json', email: email }
    };
    return requestFn(requestOptions)
      .use()
      .run()
      .then((request) => {
        let json = request.response;
        json.email = email;
        return json;
      })
      .catch(error => {
        throw error;
      });
  }

  /**
   * Logs the user out of from arrow.com BE
   */
  siteLogout() {
    this.clearCachedCustomerNumbers();
    return $.ajax({
      type: 'GET',
      url: config.urls.users.logout
    }).then(function (data) {
      return data.data;
    });
  }

  /**
  * Logs the user out of cross domain sites if they are available
  */
  crossSiteLogout(data) {
    if (data.domainAuthenticationUrls) {
      var urlCount = data.domainAuthenticationUrls.length;
      var currentCount = 0;
      data.domainAuthenticationUrls.forEach(function (urlToCall) {
        // loop through the external api calls to make and invoke them not caring about what they return
        $.ajax({
          type: 'POST',
          url: urlToCall,
          data: null,
          xhrFields: { withCredentials: true },
          contentType: 'application/x-www-form-urlencoded'
        }).done(function () {
          currentCount++;
          if (urlCount === currentCount) {
            mediator.trigger(config.events.logout.crossDomainRequestCompletion, { data });
          }
        });
      });
    }
  }


  /**
   * If the getUserInfo request errors we need to keep
   * track of it and inform listeners through mediator
   *
   * @param errorInterface This should be the standard error interface
   */
  getUserInfoRequestFailed(errorInterface) {
    if (this.userInfoError) return;
    this.userInfoError = errorInterface;
    mediator.trigger(config.events.cart.userInfoError);
  }

  /**
   * Get the current user based on the cart server's session state.
   *
   * This is an expensive call when it goes to the server so we
   * need to use it in the background
   *
   * @param {boolean} handleErrorLocally
   * @return {Promise<UserInfoData>}
   */
  getUserInfo() {
    // for hybris, return prefill guest user info if user didn't login
    if (featureFlags.EnableHybrisIntegration.isEnabled() && !this.isHybrisAuthenticated()) {
      this.userInfo = { fullName: 'Guest', email: '' };
      return Promise.resolve(this.userInfo);
    }

    let self = this;

    // If there isn't any userInfo or the name is guest,
    // but they are logged in we need to retrieve the data.
    const isValidUserInfo = this.userInfo && this.isUserLoggedIn() && !this.isCartUserAGuest();
    if (isValidUserInfo) {
      return new Promise((resolve) => {
        resolve(this.userInfo);
      });
    } else {
      this.userInfoError = null;
      let p;
      if (featureFlags.EnableHybrisIntegration.isEnabled()) {
        p = this.hybrisGetCurrentUserInfoRequest();
      }
      else {
        p = this.getCurrentUserInfoRequest();
      }

      let currentUser;
      if (featureFlags.EnableB2BUsersApiCall.isEnabled() && p && p.response) {
        currentUser = p.response;
      } else {
        currentUser = utilities.sessionStorageLoad('b2busers');
      }
      if (currentUser) {
        this.userInfo = currentUser;
        this.cacheCustomerNumbers(this.userInfo && this.userInfo.customerNumbers);
        return Promise.resolve(this.userInfo);
      } else {
        return p.run()
          .catch((err) => {
            this.userInfo = { fullName: 'Guest', email: '' };
            this.getUserInfoRequestFailed(err.request.error);
          })
          .then(() => {
            this.cacheCustomerNumbers(this.userInfo && this.userInfo.customerNumbers);
            return this.userInfo;
          });
      }
    }
  }

  /**
   * Get current user's customer numbers, cache in LocalStorage if applicable
   */
  getUserCustomerNumbers() {
    if (!this.isUserLoggedIn() || this.isCartUserAGuest()) {
      return new Promise(resolve => {
        resolve(null);
      });
    }

    let customerNumbers = null;
    try {
      customerNumbers = JSON.parse(window.localStorage.getItem(config.customerSpecificPricing.localStorageName));
    } catch (e) { }
    if (customerNumbers) {
      return new Promise(resolve => {
        resolve(customerNumbers);
      });
    } else {
      return new Promise((resolve, reject) => {
        this.getUserInfo().then(userInfo => {
          this.cacheCustomerNumbers(userInfo.customerNumbers);
          resolve(window.localStorage.customerNumbers);
        }).catch(error => {
          reject(error);
        });
      });
    }
  }

  cacheCustomerNumbers(customerNumbers) {
    if (customerNumbers && customerNumbers.arrowna) {
      window.localStorage.setItem(config.customerSpecificPricing.localStorageName, JSON.stringify(customerNumbers));
    }
  }

  /**
   * Clear cached customer numbers out of LocalStorage
   */
  clearCachedCustomerNumbers() {
    window.localStorage.removeItem(config.customerSpecificPricing.localStorageName);
  }

  /**
   * Create a SF case to link the account
   *
   *
   * @param {Object} data
   * @return {Promise<Object>}
   */
  linkAccount(data) {
    const requestOptions = {
      url: config.urls.cart.linkAccount,
      dataType: 'json',
      timeout: 20000,
      data: data,
      method: 'post',
      handleErrorLocally: true
    };
    makeRequest(requestOptions).use()
      .run()
      .catch(() => {
        // We are not doing anything is this call fails. It's just an attempt to help customers quicken the account linking.
        return true;
      });
  }

  set(obj) {
    _.extend(this, obj);
  }

  /**
   * hybris get user info
   */
  hybrisGetCurrentUserInfoRequest() {
    let shouldStartNewRequest = true;
    if (this.userInfoFetchRequest) {
      if (!this.userInfoFetchRequest.isActivated()) shouldStartNewRequest = false;
      if (this.userInfoFetchRequest.isRunning) shouldStartNewRequest = false;
    }
    if (shouldStartNewRequest) {
      const requestOptions = {
        url: `${config.hybris.baseUrl}b2busers?fields=FULL`,
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
        method: 'GET',
        async: false,
        retryOptions: {
          enabled: true,
          attempts: 2
        },
        handleErrorLocally: true
      };
      this.userInfoFetchRequest = hybrisMakeRequest(requestOptions);
      this.userInfoFetchRequest.use((request) => {
        if (featureFlags.EnableB2BUsersApiCall.isEnabled()) {
          const b2busersData = _.omit(request.response, config.b2bSecretKeys);
          utilities.sessionStorageSave('b2busers', b2busersData);
        } else {
          utilities.sessionStorageSave('b2busers', request.response);
        }
        const data = request.response;
        if (featureFlags.EnableOAuthEndpoint.isEnabled()) {
          window.localStorage.currentUserFirstName = data.firstName;
        }
        if (featureFlags.EnableB2bUserBEValues.isEnabled()) {
          cookies.set(config.cookies.countryCode, data.country.isocode, { domain: config.cookies.domain });
          cookies.set(config.cookies.cloudUserId, data.cloudUserId, { domain: config.cookies.domain });
          if (data.groups && featureFlags.EnableCustomerPriceGroupTracking.isEnabled()) {
            cookies.set(config.cookies.customerGroups, data.groups.join(','), { domain: config.cookies.domain });
            if (data.priceGroup && data.priceGroup.priceGroup && featureFlags.EnableLoyaltyMVP.isEnabled()) {
              cookies.set(config.cookies.customerPriceGroup, data.priceGroup.priceGroup.toLowerCase(), { domain: config.cookies.domain });
            }
          }
          if (cacheCookieHelper) {
            cacheCookieHelper.setCacheCookie();
          }
        }

        this.userInfo = this._fromData(data);
        if (this.userInfo) {
          let userData = { custNumber: this.userInfo.custNumber ? this.userInfo.custNumber : '-1' };
          let userObject = {
            user: {
              profile: userData
            }
          };
          Analytics.setOnDataLayer(userObject);
        }
        mediator.trigger(config.events.cart.userInfoSet, this.userInfo);
      },
        () => {
          this.userInfo = { fullName: 'Guest', email: '' };
        });
    }
    return this.userInfoFetchRequest;
  }

  isHybrisAuthenticated() {
    const arrowFed = cookies.get(config.cookies.arrowFed);
    if (arrowFed) {
      return true;
    }

    return false;
  }

  /**
   * convert hybris data to legacy format
   * @param {any} data
   */
  _fromData(data) {
    let userInfo = {};
    userInfo.email = data.uid;
    userInfo.custType = data.customerType;
    userInfo.firstName = data.firstName;
    userInfo.lastName = data.lastName;
    userInfo.fullName = data.name;
    userInfo.company = data.companyName;
    userInfo.custNumber = data.customerId;
    userInfo.terms = data.terms;
    userInfo.blocked = data.blocked;
    userInfo.blockedReason = data.blockedReason;
    const language = data.language && data.language.isocode;
    userInfo.language = language || '';
    userInfo.phone = data.phoneNumber;
    userInfo.arrowId = data.arrowCustomerId;
    userInfo.regions = data.regions || [];
    userInfo.hasAllDefaults = data.hasAllDefaults;
    userInfo.customerPriceGroup = data.customerPriceGroup,
      userInfo.customerNumbers = data.customerNumbers || '';
    userInfo.isrCustomer = data.isrCustomer;
    userInfo.sellerCustomer = data.sellerCustomer;
    userInfo.defaultPaymentMethod = data.defaultPaymentMethod;
    userInfo.customerSpecificPricing = data.customerSpecificPricing;
    userInfo.loggedIn = !!userInfo.email;
    userInfo.creditLimit = data.creditLimit;
    userInfo.groups = data.groups || [];
    if (data.country) {
      userInfo.countryCode = data.country.isocode;
    }
    userInfo.cloudUserId = data.cloudUserId;
    return userInfo;
  }
}

export const userService = new CartUserService();
export default userService;



// WEBPACK FOOTER //
// ./assets/services/CartUserService.js